import React, { useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Dialog, Progress, useToast } from '@economic/taco';
import { refreshCurrentLegacyPage } from '@economic/react-app';
import { useEmailTemplates } from '../../invoices/utils/useEmailTemplates';
import { useLocalization, t, tBB } from '../../../../../shared/components/l10n/l10n';
import { BulkBookAndSendReminderDialog, BulkBookAndSendReminderFormData } from './BulkBookAndSendReminderDialog';
import useQuery from '../../../../../shared/hooks/useQuery';
import * as internalApi from '../../../../../../types/internal-api';
import internalApiClient from '../../../../../shared/util/api/internalApiClient';
import useLocalStorage from '../../../../../shared/hooks/useLocalStorage';
import { emailOptionsFromTemplates } from '../../invoices/utils/emailOptionsFromTemplates';
import { emailTemplateFormState } from '../../invoices/utils/emailTemplateFormState';
import { InvoiceLayoutType } from '../../../../../../types/internal-api-extra';

function BulkBookAndSendReminderDialogContainer() {
    const toast = useToast();
    const location = useLocation();
    const navigate = useNavigate();
    const { state = {} } = location;
    const { draftId, customerNumbers, isBatched, bookAllBatches } = state as any;

    const [skipBookOnlyConfirmation, setSkipBookOnlyConfirmation] = useLocalStorage<boolean>('skipBulkBookOnlyConfirmation');
    const [sending, setSending] = useState<boolean>(false);
    const interruptSending = useRef<boolean>(false);
    const [sendingStatus, setSendingStatus] = useState<{
        processedCount: number;
        totalCount: number;
    }>({
        processedCount: 0,
        totalCount: 0,
    });

    const { loading, data: information } = useQuery<internalApi.GetBulkReminderEmailOptionsResponse>(
        `/sales/reminder/bulk-reminder-email-options`,
        null,
        {
            skip: !draftId,
        }
    );

    const { loading: batchInfoLoading, data: batchInfo } = useQuery<internalApi.BatchedReminderBookingInfo>(
        `/sales/reminder/batched-reminder-booking-information?draftId=${draftId}`,
        null,
        {
            skip: !draftId || !isBatched,
        }
    );

    const { data: templates, loading: loadingTemplates } = useEmailTemplates(InvoiceLayoutType.Reminder);

    const loadingTexts = useLocalization(textIds);

    const transformedInformation = useMemo<BulkBookAndSendReminderFormData | undefined>(() => {
        if (!information || (isBatched && !batchInfo)) {
            return undefined;
        }

        const batchSize = isBatched ? batchInfo.batchSize : customerNumbers.length;
        return {
            bookOnly: false,
            skipBookOnlyConfirmation: skipBookOnlyConfirmation,
            isBatched: isBatched,
            bookAllBatches: bookAllBatches,
            batchSize: batchSize,
            remindersToSendCount: isBatched && bookAllBatches ? batchInfo.totalCustomersCount : batchSize,
            customerIdBatches: isBatched ? batchInfo.customerIdBatches : [customerNumbers.map(Number)],
            emailOptions: {
                ...information,
                saveSubject: false,
                saveBody: false,
                showPdf: isBatched && bookAllBatches ? false : information.showPdf,
                ...emailOptionsFromTemplates(templates),
            },
            ...emailTemplateFormState(templates),
        };
    }, [information, batchInfo, templates]);

    async function bookAndSend(values: BulkBookAndSendReminderFormData) {
        try {
            setSending(true);
            setSendingStatus({
                processedCount: 0,
                totalCount: values.remindersToSendCount,
            });

            const request: internalApi.BulkBookAndSendReminderRequest = {
                customerIds: [],
                draftNumber: draftId,
                sendEmail: !values.bookOnly,
                showAfterSending: values.emailOptions.showPdf,
                subject: values.emailOptions.subject,
                saveSubject: values.emailOptions.saveSubject,
                body: values.emailOptions.body,
                saveBody: values.emailOptions.saveBody,
                includePdfLink: values.emailOptions.includePdfLink,
                includePdfAsAttachment: values.emailOptions.includePdfAsAttachment,
                encrypt: values.emailOptions.encrypt,
            };

            let currentBatchIndex = 0;
            let bookedRemindersCount = 0;
            let skippedCustomersCount = 0;
            let pdfLink;
            do {
                if (interruptSending.current) {
                    break;
                }

                request.customerIds = values.customerIdBatches[currentBatchIndex];
                const result = await internalApiClient.post<internalApi.ReminderBookingResult>(
                    `/sales/reminder/book-and-send-bulk-reminders`,
                    request
                );

                setSendingStatus(current => {
                    return {
                        ...current,
                        processedCount: current.processedCount + request.customerIds.length,
                    };
                });

                if (result.data) {
                    if (result.data.validationErrors?.length > 0) {
                        toast.error(result.data.validationErrors[0]);
                        break;
                    }

                    skippedCustomersCount += result.data.skippedCustomers?.length ?? 0;
                    bookedRemindersCount += result.data.bookedReminders?.length ?? 0;
                    pdfLink = result.data.pdfLink;
                }

                currentBatchIndex++;
            } while (!!values.isBatched && !!values.bookAllBatches && values.customerIdBatches.length > currentBatchIndex);

            if (bookedRemindersCount > 0) {
                handleSuccess(bookedRemindersCount, pdfLink, values);
            }

            if (skippedCustomersCount) {
                toast.error(t('TOAST_REMINDER_BOOK_AND_SEND_PLURAL_FAILED_TO_SEND', { COUNT: skippedCustomersCount }));
            }

            setSkipBookOnlyConfirmation(values.skipBookOnlyConfirmation);
            setSending(false);

            refreshContentFrameAfterSuccess();
            hide();
        } catch (error) {
            //
        }
    }

    function handleSuccess(bookedRemindersCount: number, pdfLink: string, values: BulkBookAndSendReminderFormData) {
        if (bookedRemindersCount > 0) {
            const toastTextId = values.bookOnly
                ? 'TOAST_REMINDER_BOOK_PLURAL_SUCCESS'
                : 'TOAST_REMINDER_BOOK_AND_SEND_PLURAL_SUCCESS';

            const goToLink = values.bookOnly ? '/sales/invoicing/archive' : '/sales/invoicing/sent';

            const toastContent = tBB(toastTextId, {
                COUNT: bookedRemindersCount,
                __LINK: content => <Link to={goToLink}>{content}</Link>,
            });

            toast.success(toastContent);

            if (values.emailOptions.showPdf && pdfLink) {
                window.top && window.top.firebirdTask.then(() => window.top?.PopUp2(pdfLink, 500, 600));
            }
        }
    }

    function refreshContentFrameAfterSuccess() {
        refreshCurrentLegacyPage('content');
    }

    function hide() {
        navigate(location.pathname);

        if (sending) {
            interruptSending.current = true;
        }
    }

    if (!draftId) {
        return null;
    }

    const open = location.hash === '#book-bulk-reminder';

    if (sending && transformedInformation && transformedInformation.isBatched && transformedInformation.bookAllBatches) {
        return (
            <Dialog open={open} onClose={hide} size="xs">
                <Dialog.Content aria-label={t`TOAST_LOADING`}>
                    <Dialog.Title>{t(`FORM_BOOKANDSEND_BULK_REMINDER_HEADER`)}</Dialog.Title>
                    <div className="py-8">
                        <div className="w-full">
                            <span>
                                {t('FORM_BOOKANDSEND_BULK_REMINDER_PROCESSEDPROGRESS', {
                                    PROCESSED: sendingStatus.processedCount,
                                    TOTAL: sendingStatus.totalCount,
                                })}
                            </span>
                        </div>
                        <Progress className="mt-4" />
                    </div>
                </Dialog.Content>
            </Dialog>
        );
    }

    return (
        <BulkBookAndSendReminderDialog
            open={open}
            formData={transformedInformation}
            loading={loading || (isBatched && batchInfoLoading) || loadingTexts || loadingTemplates || sending}
            onBookAndSend={bookAndSend}
            onHide={hide}
        />
    );
}

const textIds = [
    'FORM_BOOKANDSEND_BULK_REMINDER_HEADER',
    'FORM_BOOKANDSEND_BULK_REMINDER_TEXT',
    'FORM_BOOKANDSEND_BULK_REMINDER_BOOKANDSEND',
    'FORM_BOOKANDSEND_BULK_REMINDER_BOOKONLY',
    'FORM_BOOKANDSEND_BULK_REMINDER_BOOK_ONLY_CONFIRMATION_TEXT',
    'FORM_BOOKANDSEND_BULK_REMINDER_BOOK_ONLY_CONFIRMATION_DO_NOT_SHOW',
    'FORM_BOOKANDSEND_BULK_REMINDER_BACK',
    'TOAST_REMINDER_BOOK_AND_SEND_PLURAL_SUCCESS',
    'TOAST_REMINDER_BOOK_PLURAL_SUCCESS',
    'TOAST_REMINDER_BOOK_AND_SEND_PLURAL_FAILED_TO_SEND',
    'FORM_BOOKANDSEND_BULK_REMINDER_VALIDATION_FAILURE_HEADER',
    'FORM_BOOKANDSEND_BULK_REMINDER_SKIPPED_CUSTOMERS_HEADER',
    'FORM_BOOKANDSEND_SHOW_REMINDER',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_OPEN',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_CLOSE',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_RECIPIENTS',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_TOGGLE_CC_BCC',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_CC',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_BCC',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_SUBJECT',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_BODY',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_REMEMBER',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_OPEN',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_CLOSE',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_PDF_ATTACHMENT',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_PDF_ATTACHMENT',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_ENCRYPT',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_PDF_LINK',
    'FORM_BOOKANDSEND_SELECT_ATTACHMENT_ERROR',
    'FORM_BOOKANDSEND_SELECT_EMAIL_RECIPIENT_ERROR',
    'POPUP_INVOICEETC_ERROR_INVALIDEMAIL',
    'FORM_BOOKANDSEND_ERRORS_HEADER',
    'NOTIFICATION_SUCCESS',
    'FORM_BOOKANDSEND_ERRORS_UNKNOWN_ERROR',
    'FORM_BOOKANDSEND_BULK_REMINDER_PROCESSEDPROGRESS',
];

export default BulkBookAndSendReminderDialogContainer;
